import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MdLocalPhone, MdLocationOn, MdWarning, MdSend } from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocation as useHcenLocation } from '../context/LocationContext';

const EMERGENCY_TYPES = [
  { id: 'accident',    icon: '🚗', label: 'Accident',          color: '#ef4444', apiLabel: 'Road Accident' },
  { id: 'cardiac',     icon: '❤️', label: 'Heart Attack',      color: '#dc2626', apiLabel: 'Chest Pain' },
  { id: 'breathing',   icon: '🫁', label: 'Breathing Problem', color: '#f97316', apiLabel: 'Breathing Problem' },
  { id: 'unconscious', icon: '😵', label: 'Unconscious',       color: '#a855f7', apiLabel: 'Unconscious' },
  { id: 'stroke',      icon: '🧠', label: 'Stroke',            color: '#6366f1', apiLabel: 'Stroke' },
  { id: 'burn',        icon: '🔥', label: 'Burns',             color: '#f59e0b', apiLabel: 'Burns' },
  { id: 'fall',        icon: '🤕', label: 'Fall / Fracture',   color: '#0ea5e9', apiLabel: 'Fall / Fracture' },
  { id: 'other',       icon: '🏥', label: 'Other Emergency',   color: '#64748b', apiLabel: 'Other Emergency' },
];

export default function EmergencyPage() {
  const [searchParams] = useSearchParams();
  const resumeAlertId = searchParams.get('alertId');
  const resumeBookingId = searchParams.get('bookingId');

  const { user, showToast } = useAuth();
  // Big-company pattern: emergencies use the SAME cached coords the rest of
  // the app already detected (chip / home page). User does NOT have to re-tap
  // "Detect" — every second matters. We still expose `getLocation()` below as
  // a manual refresh if they want a fresh fix.
  const hcenLoc = useHcenLocation();

  const [pulse,        setPulse]        = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [location,     setLocation]     = useState(() => {
    // Pre-fill with cached coords + city the moment the page mounts.
    if (hcenLoc?.coords) {
      const { lat, lng } = hcenLoc.coords;
      const city = hcenLoc.city ? ` (${hcenLoc.city})` : '';
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}${city}`;
    }
    return '';
  });
  const [description,  setDescription]  = useState('');
  const [patientName,  setPatientName]  = useState(user?.name || '');
  const [sending,      setSending]      = useState(false);
  const [sent,         setSent]         = useState(!!resumeAlertId);
  const [alertId,      setAlertId]      = useState(resumeAlertId);
  const [alertStatus,  setAlertStatus]  = useState(null);
  const [cancellingEm, setCancellingEm] = useState(false);
  const [locLoading,   setLocLoading]   = useState(false);

  // Pulse animation cycle
  useEffect(() => {
    const t = setInterval(() => setPulse(p => !p), 900);
    return () => clearInterval(t);
  }, []);

  // Live poll emergency status when alert is active
  useEffect(() => {
    if (!alertId) return;
    let active = true;
    const poll = async () => {
      while (active && alertId) {
        try {
          const r = await api.getEmergencyStatus(alertId);
          if (r.data && active) setAlertStatus(r.data);
          if (['handled', 'cancelled', 'hospital_declined'].includes(r.data?.status)) break;
        } catch {}
        await new Promise(ok => setTimeout(ok, 3000));
      }
    };
    poll();
    return () => { active = false; };
  }, [alertId]);

  const handleCancelEmergency = async () => {
    if (!alertId) return;
    setCancellingEm(true);
    await api.cancelEmergency(alertId);
    setCancellingEm(false);
    setSent(false); setAlertId(null); setAlertStatus(null);
    showToast('Emergency cancelled', 'success');
  };

  const getLocation = async () => {
    setLocLoading(true);
    // Prefer the shared LocationContext.requestGps — it handles permission
    // states, Permissions API, and updates the global cache so OTHER pages
    // (home, hospital list) instantly see the fresh coords too.
    const next = hcenLoc?.requestGps ? await hcenLoc.requestGps() : null;
    if (next?.coords) {
      const { lat, lng } = next.coords;
      const cityTag = next.city ? ` (${next.city})` : '';
      setLocation(`${lat.toFixed(5)}, ${lng.toFixed(5)}${cityTag}`);
      setLocLoading(false);
      return;
    }
    // Fallback: direct browser API (e.g. context not mounted for some reason).
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        setLocation(`${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`);
        setLocLoading(false);
      },
      () => { setLocLoading(false); showToast('Could not get location', 'error'); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSendAlert = async () => {
    if (!selectedType) { showToast('Select emergency type first', 'error'); return; }
    let lat = hcenLoc?.coords?.lat;
    let lng = hcenLoc?.coords?.lng;
    if (lat == null || lng == null) {
      const loc = hcenLoc?.requestGps ? await hcenLoc.requestGps() : null;
      lat = loc?.coords?.lat;
      lng = loc?.coords?.lng;
    }
    if (lat == null || lng == null) {
      showToast('GPS location required for SOS. Allow location access and try again.', 'error');
      return;
    }
    const typeMeta = EMERGENCY_TYPES.find(t => t.id === selectedType);
    setSending(true);
    const emergResult = await api.sendSosBroadcast({
      patientName:   patientName || user?.name || 'HCEN User',
      patientId:     user?.patientId || user?.phone || 'guest',
      phone:         user?.phone || '',
      emergencyType: typeMeta?.apiLabel || selectedType,
      description:   description || typeMeta?.label || '',
      patientLat:    lat,
      patientLng:    lng,
    });
    setSending(false);
    if (emergResult.error) {
      showToast(emergResult.error, 'error');
      return;
    }
    if (emergResult.data?.alertId) setAlertId(emergResult.data.alertId);
    setSent(true);
    showToast('SOS sent — searching nearby hospitals (5 km, expanding every 45s)', 'success');
  };

  // ── Success screen ──────────────────────────────────────────
  const STATUS_STEPS = [
    { label: 'Alert Sent',            icon: '📡', desc: 'Broadcasting to nearby hospitals (5→50 km every 45s)…' },
    { label: 'Hospital Notified',     icon: '🏥', desc: 'A hospital has acknowledged your alert!' },
    { label: 'Preparing for Arrival', icon: '🚑', desc: 'Hospital is preparing for your arrival.' },
    { label: 'Ready — Come In',       icon: '✅', desc: 'Hospital is ready. Please come in immediately.' },
  ];
  const normStatus = alertStatus?.status || 'pending';
  const stepIdx = (() => {
    if (normStatus === 'pending' || normStatus === 'new') return 0;
    if (normStatus === 'acknowledged') return 1;
    if (normStatus === 'preparing') return 2;
    if (normStatus === 'handled') return 3;
    return 0;
  })();
  const isSearching = normStatus === 'pending' || normStatus === 'new';

  if (sent) return (
    <div className="page fade-in" style={{ maxWidth: 480 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 64, marginBottom: 12 }} className="status-live">🚨</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#ef4444', marginBottom: 4 }}>Emergency Active</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Live tracking - updates every 3s</div>
        {resumeBookingId && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            Booking ID: <strong style={{ fontFamily: 'monospace' }}>{resumeBookingId}</strong>
          </div>
        )}
      </div>

      {/* Live Status Timeline */}
      <div className="glass glass-p" style={{ marginBottom: 20 }}>
        {STATUS_STEPS.map((step, i) => {
          const done = i <= stepIdx;
          const active = i === stepIdx;
          return (
            <div key={step.label} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: i < STATUS_STEPS.length - 1 ? 16 : 0, position: 'relative' }}>
              {i < STATUS_STEPS.length - 1 && <div style={{ position: 'absolute', left: 16, top: 34, width: 2, height: 'calc(100% - 8px)', background: done ? 'var(--green)' : 'var(--border)' }} />}
              <div style={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: done ? 'var(--green-pale)' : '#f5f5f5', border: active ? '2px solid var(--green)' : '2px solid var(--border)', flexShrink: 0, zIndex: 1 }}>
                {step.icon}
              </div>
              <div style={{ flex: 1, paddingTop: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: done ? 'var(--text)' : 'var(--text-muted)' }}>{step.label}</div>
                {active && <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 2 }}>{step.desc}</div>}
              </div>
              {active && <div className="status-live" style={{ fontSize: 10, padding: '2px 8px', borderRadius: 12, background: 'var(--green-pale)', color: 'var(--green)', fontWeight: 700 }}>LIVE</div>}
            </div>
          );
        })}
      </div>

      {/* SOS search progress */}
      {isSearching && alertStatus?.broadcastMode && (
        <div className="glass glass-p" style={{ marginBottom: 16, border: '1px solid rgba(239,68,68,0.25)' }}>
          <div style={{ fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>📍 Searching nearby hospitals</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Current radius: <strong>{alertStatus.currentRadiusKm ?? 5} km</strong>
            {alertStatus.hospitalsInRadius != null && (
              <> · <strong>{alertStatus.hospitalsInRadius}</strong> hospitals can see your alert</>
            )}
            {Array.isArray(alertStatus.declined) && alertStatus.declined.length > 0 && (
              <> · <strong>{alertStatus.declined.length}</strong> declined</>
            )}
          </div>
        </div>
      )}

      {/* Hospital Info (if acknowledged) */}
      {alertStatus?.hospitalName && !isSearching && (
        <div className="glass glass-p" style={{ marginBottom: 16, border: '1px solid var(--green-border)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--green)' }}>🏥 Responding Hospital</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{alertStatus.hospitalName}</div>
          {alertStatus.hospitalPhone && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>📞 {alertStatus.hospitalPhone}</div>}
        </div>
      )}

      {Array.isArray(alertStatus?.declined) && alertStatus.declined.length > 0 && (
        <div className="glass glass-p" style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-muted)' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Hospitals that declined</div>
          {alertStatus.declined.map((d, i) => (
            <div key={i}>• {d.hospitalName || 'Hospital'}{d.reason ? ` — ${d.reason}` : ''}</div>
          ))}
        </div>
      )}

      <a href="tel:108" className="btn btn-danger btn-full btn-lg" style={{ marginBottom: 10, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <MdLocalPhone size={22} /> Call 108 Ambulance Now
      </a>
      {normStatus !== 'handled' && (
        <button className="btn btn-secondary btn-full" onClick={handleCancelEmergency} disabled={cancellingEm}>
          {cancellingEm ? 'Cancelling...' : 'Cancel Emergency'}
        </button>
      )}
    </div>
  );

    return (
    <div className="page fade-in" style={{ maxWidth: 520 }}>

      {/* ── SOS 108 BUTTON ───────────────────────────────── */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <a href="tel:108" style={{ display: 'inline-block', textDecoration: 'none' }}>
          <div style={{
            width: 170, height: 170, borderRadius: '50%', margin: '0 auto',
            background: 'radial-gradient(circle at 40% 35%, #ef4444, #7f1d1d)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', position: 'relative',
            boxShadow: pulse
              ? '0 0 0 22px rgba(239,68,68,0.15), 0 0 0 44px rgba(239,68,68,0.06), 0 0 60px rgba(239,68,68,0.5)'
              : '0 0 0 10px rgba(239,68,68,0.25), 0 0 35px rgba(239,68,68,0.25)',
            transition: 'box-shadow 0.7s ease',
          }}>
            <MdLocalPhone size={52} color="white" />
            <div style={{ color: 'white', fontWeight: 900, fontSize: 34, lineHeight: 1 }}>108</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, letterSpacing: 1, marginTop: 2 }}>
              TAP TO CALL
            </div>
          </div>
        </a>
        <div style={{ marginTop: 16, color: 'var(--text-muted)', fontSize: 13 }}>
          🚑 Free ambulance · Available 24/7
        </div>
      </div>

      {/* ── Alert Form ───────────────────────────────────── */}
      <div className="glass" style={{ borderRadius: 20, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{
          padding: '14px 20px',
          background: 'rgba(239,68,68,0.10)',
          borderBottom: '1px solid rgba(239,68,68,0.18)',
        }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#f87171' }}>
            📡 Alert Nearby Hospitals
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Notify all hospitals in your area instantly
          </div>
        </div>

        <div style={{ padding: 20 }}>

          {/* Emergency Type Grid */}
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Emergency Type *</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
            {EMERGENCY_TYPES.map(t => (
              <button key={t.id} onClick={() => setSelectedType(t.id)} style={{
                padding: '10px 4px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: selectedType === t.id ? t.color : 'var(--bg-card)',
                color: selectedType === t.id ? 'white' : 'var(--text-muted)',
                fontSize: 10, fontWeight: 600, textAlign: 'center',
                transition: 'all 0.2s',
                transform: selectedType === t.id ? 'scale(1.06)' : 'scale(1)',
                boxShadow: selectedType === t.id ? `0 4px 14px ${t.color}55` : 'none',
              }}>
                <div style={{ fontSize: 22, marginBottom: 3 }}>{t.icon}</div>
                {t.label}
              </button>
            ))}
          </div>

          {/* Patient Name */}
          <div className="input-group">
            <label className="input-label">Your Name</label>
            <input className="input" placeholder="Patient / caller name"
              value={patientName} onChange={e => setPatientName(e.target.value)} />
          </div>

          {/* Location */}
          <div className="input-group">
            <label className="input-label">Location</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" style={{ flex: 1 }}
                placeholder="Area, landmark or address"
                value={location} onChange={e => setLocation(e.target.value)} />
              <button className="btn btn-secondary" onClick={getLocation}
                disabled={locLoading} style={{ flexShrink: 0, padding: '0 16px' }}>
                {locLoading ? '⏳' : <MdLocationOn size={20} />}
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="input-group">
            <label className="input-label">Brief Description</label>
            <textarea className="input" rows={2}
              placeholder="e.g. Chest pain, breathing difficulty, bleeding…"
              value={description} onChange={e => setDescription(e.target.value)}
              style={{ resize: 'none' }} />
          </div>

          <button className="btn btn-danger btn-full btn-lg"
            onClick={handleSendAlert} disabled={sending || !selectedType}
            style={{ marginTop: 4 }}>
            {sending
              ? '⏳ Sending Alert…'
              : <><MdSend style={{ marginRight: 6 }} />Send Emergency Alert</>}
          </button>
        </div>
      </div>

      {/* ── Footer warning ──────────────────────────────── */}
      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', padding: '0 8px' }}>
        <MdWarning style={{ color: '#f59e0b', verticalAlign: 'middle', marginRight: 4 }} />
        In life-threatening emergencies, always call{' '}
        <strong style={{ color: '#ef4444' }}>108</strong> immediately.
      </div>
    </div>
  );
}
